import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import colorPallete, { bodyFonts, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    useGetLink, useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Link from "@insite/mobius/Link/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    categoryData = "categoryData",
}

interface CategoryItem {
    fields: {
        categoryTitle: string;
        categoryUrl: string; 
        categoryImage: string;
        categoryImageAltText: string;
    }
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.categoryData]: CategoryItem[];
    };
}

export interface DSGShopTopCategoriesStyles {
    shopBySectionWrapper?: InjectableCss;
    shopByColumnWrapper?: InjectableCss;
    shopByColumnItem?: InjectableCss;
}

export const dsgShopTopCategoriesStyles: DSGShopTopCategoriesStyles = {
    shopBySectionWrapper: {
        css: css`
            text-align: center;
            max-width: 1280px;
            margin: 0 auto;
            .innerWrapper {
                margin: 40px 0 !important;
            }
            h2 {
                margin-bottom: 24px;
                padding-left: 8px;
                padding-right: 8px;
            }
            @media screen and (min-width: 768px) and (max-width: 1280px) {
                .innerWrapper {
                    margin-left: 24px !important;
                    margin-right: 24px !important;
                }
           }
        `
    },
    shopByColumnWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                null,
                css`
                    & {
                        gap: 40px;
                    }
                `,
            ], "min")}
        `
    },
    shopByColumnItem: {
        css: css`
            max-width: calc(50% - 24px);
            width: calc(50% - 24px);
            display: flex;
            border-radius: 8px;
            border: 1px solid ${colorPallete.grey300};
            background: ${colorPallete.white};
            padding: 16px;
            a {
                width: 100%;
                justify-content: center;
                align-items: flex-start;
                > span {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                    color: ${colorPallete.grey800};
                    font: ${bodyFonts.body1SemiBoldLg.fontWeight} ${bodyFonts.body1SemiBoldLg.fontSize}/${bodyFonts.body1SemiBoldLg.lineHeight} ${bodyFonts.body1SemiBoldLg.fontFamily};   
                }
                img {
                    max-height: 120px;
                    min-height: 120px;
                    width: auto;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    & {
                        width: calc(100%/4 - 12px);
                        max-width: 244px;
                    }
                    a {       
                        img {
                            max-height: 150px;
                            min-height: 150px;
                            height: auto;
                            width: 100%;
                        }
                    }
                `,
                css`
                    & {
                        width: calc(100%/4 - 30px);
                    }
                   
                `,
            ], "min")}
        `
    },
};

const styles = dsgShopTopCategoriesStyles;

export const DSGShopTopCategories: FC<OwnProps> = ({ fields }) => {

    const { categoryData } = fields;

    return (
        <StyledWrapper {...styles.shopBySectionWrapper}> 
            <StyledWrapper className="innerWrapper">
                {fields.title && (
                    <Typography  className="heading2" as="h2" variant="h2" >
                        {fields.title}
                    </Typography>
                )}
                {categoryData.length > 0 && (
                    <StyledWrapper {...styles.shopByColumnWrapper}>
                        {categoryData.map((item, index) => (
                            <StyledWrapper {...styles.shopByColumnItem}>
                                {item.fields.categoryUrl && (
                                    <Link href={item.fields.categoryUrl} className="text-link text-link-secondary">
                                        {item.fields.categoryImage && (
                                            <Img src={item.fields.categoryImage} altText={item.fields.categoryImageAltText || ''} />   
                                        )}
                                        {item.fields.categoryTitle && (
                                            <Typography>
                                                {parse(item.fields.categoryTitle, parserOptions)}
                                            </Typography>
                                        )}
                                    </Link>
                                )}
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                )}
            </StyledWrapper>
         
        </StyledWrapper>
    );
};



const widgetModule: WidgetModule = {
    component: DSGShopTopCategories,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.categoryData,
                editorTemplate: "ListField",
                displayName: "Category Details",
                getDisplay: (item: HasFields) => {
                    const {
                        categoryTitle,
                    } = item.fields;
                    if (categoryTitle) {
                        return categoryTitle;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "categoryTitle",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "categoryUrl",
           				editorTemplate: "TextField",
                        displayName: "URL",
						defaultValue: "",
                        sortOrder: 1,
                    },
                    {
                        name: "categoryImage",
                        displayName: 'Image',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 2
                    },
                    {
                        name: "categoryImageAltText",
                        editorTemplate: "TextField",
                        displayName: "Category Image Alt Text",
						defaultValue: "",
                        sortOrder: 3,
                    }
                ],
            },
        ],
    },
};

export default widgetModule;
